import { useEffect, useState } from "react";
import { apiGet, apiPut, apiPost, apiDelete } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Alert, Button, Spinner, EmptyState, Input, Field } from "../components/ui";
import { ReportQueue } from "../components/ReportQueue";

/* Admin surface for the Gear Exchange: see every member, grant or revoke the
   admin role. Listing takedown lives on the listing itself (an admin viewing
   any listing gets a Remove control) rather than being duplicated here. */
export function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [busyEmail, setBusyEmail] = useState("");
  const [filter, setFilter] = useState("");

  async function load() {
    setError("");
    try {
      const { users } = await apiGet("/admin/users");
      setUsers(users);
    } catch (e) {
      setError(e.message || "Couldn't load members.");
      setUsers([]);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function setActive(target, active) {
    setBusyEmail(target.email);
    setError("");
    try {
      await apiPut("/admin/users", { email: target.email, active });
      await load();
    } catch (e) {
      setError(e.message || "Couldn't update that member.");
    } finally {
      setBusyEmail("");
    }
  }

  async function removeMember(target) {
    // Typed confirmation: this cascades and can't be undone, so it shouldn't be
    // one mis-click away.
    const typed = window.prompt(
      `Permanently delete ${target.email}?\n\n` +
        `This removes their listings, photos, conversations and alerts. It cannot ` +
        `be undone — deactivating is reversible and usually what you want.\n\n` +
        `Type DELETE to confirm.`
    );
    if (typed !== "DELETE") return;
    setBusyEmail(target.email);
    setError("");
    try {
      await apiDelete(`/admin/users?email=${encodeURIComponent(target.email)}`);
      await load();
    } catch (e) {
      setError(e.message || "Couldn't delete that member.");
    } finally {
      setBusyEmail("");
    }
  }

  async function setAdmin(target, isAdmin) {
    setBusyEmail(target.email);
    setError("");
    try {
      await apiPut("/admin/users", { email: target.email, isAdmin });
      await load();
    } catch (e) {
      setError(e.message || "Couldn't update that member.");
    } finally {
      setBusyEmail("");
    }
  }

  if (users === null) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  function InviteForm() {
    const [form, setForm] = useState({ displayName: "", email: "", isAdmin: false });
    const [inviteError, setInviteError] = useState("");
    const [notice, setNotice] = useState(null);
    const [sending, setSending] = useState(false);

    async function submit(e) {
      e.preventDefault();
      setInviteError("");
      setNotice(null);
      setSending(true);
      try {
        const res = await apiPost("/admin/users", form);
        setNotice({
          email: res.invited.email,
          resent: res.resent,
          devLink: res.devInviteLink || null,
        });
        setForm({ displayName: "", email: "", isAdmin: false });
        await load();
      } catch (err) {
        setInviteError(err.message || "Couldn't send that invite.");
      } finally {
        setSending(false);
      }
    }

    return (
      <form onSubmit={submit} className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="font-semibold text-slate-800">Invite a member</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          They'll get an email to set a password. Invited members skip the member code.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <Input
              required
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Jordan Smith"
            />
          </Field>
          <Field label="Email">
            <Input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
            />
          </Field>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.isAdmin}
            onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300"
          />
          Make this member an admin
        </label>
        {inviteError && (
          <div className="mt-3">
            <Alert>{inviteError}</Alert>
          </div>
        )}
        {notice && (
          <div className="mt-3">
            <Alert kind="success">
              {notice.resent ? "Invite re-sent to " : "Invite sent to "}
              {notice.email}.
              {notice.devLink && (
                <>
                  {" "}
                  <span className="block mt-1 break-all text-xs">
                    Dev mode — no email configured. Link: {notice.devLink}
                  </span>
                </>
              )}
            </Alert>
          </div>
        )}
        <Button type="submit" disabled={sending} className="mt-3">
          {sending ? <Spinner /> : "Send invite"}
        </Button>
      </form>
    );
  }

  const shown = users.filter((u) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      String(u.email).toLowerCase().includes(q) ||
      String(u.displayName || "").toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-xl font-bold text-slate-800">Members</h1>
        <span className="text-sm text-slate-500">
          {users.length} member{users.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Admins can remove any listing and manage who else is an admin.
      </p>

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      <ReportQueue />

      <h2 className="mt-8 text-lg font-bold text-slate-800">Members</h2>
      <InviteForm />

      {users.length > 5 && (
        <div className="mt-4">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or email"
          />
        </div>
      )}

      {shown.length === 0 ? (
        <div className="mt-6">
          <EmptyState title="No members match that search." />
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
          {shown.map((u) => {
            const isMe = user && u.email === user.email;
            const busy = busyEmail === u.email;
            return (
              <li key={u.email} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800 truncate">
                      {u.displayName || u.email}
                    </span>
                    {u.isAdmin && (
                      <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">
                        Admin
                      </span>
                    )}
                    {isMe && <span className="shrink-0 text-xs text-slate-400">you</span>}
                    {u.active === false && (
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                        Deactivated
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 truncate">{u.email}</div>
                  {!u.emailVerified && (
                    <div className="text-xs text-amber-600">Email not verified</div>
                  )}
                  {u.viaBootstrap && (
                    <div className="text-xs text-slate-400">
                      Admin via GEAR_ADMIN_EMAILS — change it in the app settings
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!isMe && (
                    <>
                      <Button
                        variant="secondary"
                        disabled={busy}
                        onClick={() => setActive(u, u.active === false)}
                      >
                        {busy ? <Spinner /> : u.active === false ? "Reactivate" : "Deactivate"}
                      </Button>
                      <Button variant="danger" disabled={busy} onClick={() => removeMember(u)}>
                        Delete
                      </Button>
                    </>
                  )}
                  {u.viaBootstrap ? (
                    <span className="text-xs text-slate-400">Locked</span>
                  ) : u.isAdmin ? (
                    <Button
                      variant="secondary"
                      disabled={busy || isMe}
                      onClick={() => setAdmin(u, false)}
                      title={isMe ? "You can't remove your own admin access" : undefined}
                    >
                      {busy ? <Spinner /> : "Remove admin"}
                    </Button>
                  ) : (
                    <Button variant="secondary" disabled={busy} onClick={() => setAdmin(u, true)}>
                      {busy ? <Spinner /> : "Make admin"}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
